import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLabOrder } from '../labs-order.page';
import { usePatchLabOrderV1LabsOrdersOrderIdPatch } from '../../../../api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { useSearchPatientServiceCentersV1LabsPatientServiceCentersGet } from '../../../../api/generated-catch-backend/patient-service-centers/patient-service-centers';
import type { PatientServiceCenterDTO } from '../../../../api/generated-catch-backend/model';
import { OrderStatus } from '../../../../api/generated-catch-backend/model';
import { ROUTE_LABS_ORDER_STEP_2, ROUTE_LABS_ORDER_STEP_5 } from '../../../../constants/routes';
import { LabsOrderStepLayout, LocationInput, FormSelect } from '../components';
import { useSubmitAnswer, formatAnswerForSubmission } from '@/app/services/questionnaireV2.service';

export function Step3SelectLab() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeOrder, isLoadingOrder, refetchOrder, source, questionnaireId, responseId } = useLabOrder();

  const submitAnswerMutation = useSubmitAnswer(questionnaireId);

  const [selectedLabSiteCode, setSelectedLabSiteCode] = useState<string | null>(null);
  const [zipCode, setZipCode] = useState<string>('');
  const [radius, setRadius] = useState<10 | 20 | 25 | 50 | 100>(50);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const patchOrderMutation = usePatchLabOrderV1LabsOrdersOrderIdPatch();

  // Initialize zip code from active order only
  useEffect(() => {
    if (zipCode) return; // Already initialized

    // Get zip code from active order
    if (activeOrder?.postalCode) {
      setZipCode(activeOrder.postalCode);
    }
  }, [activeOrder, zipCode]);

  // Prefill selected lab if order already has one
  useEffect(() => {
    if (activeOrder?.junctionSiteCode && !selectedLabSiteCode) {
      setSelectedLabSiteCode(activeOrder.junctionSiteCode);
    }
  }, [activeOrder, selectedLabSiteCode]);

  // Fetch labs based on current zip code
  // When order ID is available, the backend will use the order's state for provider filtering
  const {
    data: labsData,
    isLoading,
    error,
  } = useSearchPatientServiceCentersV1LabsPatientServiceCentersGet(
    {
      'zip-code': zipCode,
      radius: radius,
      'order-id': activeOrder?.orderId,
    },
    {
      query: {
        enabled: !!zipCode && zipCode.length === 5, // Only fetch when we have a valid 5-digit zip
      },
    },
  );

  // Sort labs by distance, nearest first
  const labs = useMemo(() => {
    const labsList = labsData?.data || [];
    return [...labsList].sort((a, b) => a.distance - b.distance);
  }, [labsData]);

  const selectedLab = useMemo(() => {
    return labs.find((lab) => lab.siteCode === selectedLabSiteCode) || null;
  }, [labs, selectedLabSiteCode]);

  const handleSelectLab = (siteCode: string) => {
    setSelectedLabSiteCode(siteCode);
    setErrorMessage(null);
  };

  const handleUseCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setErrorMessage('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    setErrorMessage(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          console.log('Got position:', position.coords.latitude, position.coords.longitude);

          // Use BigDataCloud's free reverse geocoding API (no API key required)
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`,
          );

          if (!response.ok) {
            console.error('Reverse geocoding API error:', response.status, response.statusText);
            setErrorMessage('Failed to get location. Please enter your zip code manually.');
            setIsGettingLocation(false);
            return;
          }

          const data = await response.json();
          console.log('Reverse geocoding API response:', data);

          if (data.postcode) {
            console.log('Found zip code:', data.postcode);
            setZipCode(data.postcode);
          } else {
            console.error('No postcode found in response');
            setErrorMessage('Could not determine zip code from your location');
          }
        } catch (error) {
          console.error('Error getting location:', error);
          setErrorMessage('Failed to get your location. Please try again.');
        } finally {
          setIsGettingLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setErrorMessage('Failed to get your location. Please ensure location permissions are enabled.');
        setIsGettingLocation(false);
      },
    );
  }, []);

  const handleContinue = async () => {
    if (!selectedLab) {
      setErrorMessage('Please select a lab to continue');
      return;
    }

    if (!activeOrder || !activeOrder.orderId) {
      setErrorMessage('No active order found. Please go back and complete the previous step.');
      return;
    }

    setErrorMessage(null);

    // Validate lab provider
    if (!selectedLab.labProvider) {
      setErrorMessage('Invalid lab provider. Please select a different lab.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Step 1: Select the patient service center (only if not already selected)
      // Check if order is already in psc_selected state (select_psc succeeded previously)
      const needsSelectPsc = activeOrder.status !== OrderStatus.psc_selected;

      if (needsSelectPsc) {
        await patchOrderMutation.mutateAsync({
          orderId: activeOrder.orderId,
          data: {
            operation: 'select_psc',
            data: {
              labProvider: selectedLab.labProvider,
              junctionSiteCode: selectedLab.siteCode,
              pscZipCode: selectedLab.metadata.zip,
            },
          },
        });
      }

      // Step 2: Confirm the order (triggers Junction requisition creation)
      await patchOrderMutation.mutateAsync({
        orderId: activeOrder.orderId,
        data: {
          operation: 'confirm_order',
          data: {
            consentStatus: 'accepted',
          },
        },
      });

      // Refetch order to get updated state
      await refetchOrder();

      // If this is risk assessment context, submit synthetic answer and navigate to next question
      if (source === 'risk_assessment' && questionnaireId && responseId) {
        try {
          const answerInput = formatAnswerForSubmission({
            value: 'labs_ordered_successfully',
            questionType: 'select',
            units: null,
          });

          if (!answerInput) {
            throw new Error('Failed to format completion answer');
          }

          const result = await submitAnswerMutation.mutateAsync({
            questionnaireId,
            responseId,
            questionSlug: 'labs_order_status',
            data: answerInput,
          });

          // Navigate to next question with direct flag to bypass session restoration
          if (result?.nextQuestion?.slug) {
            navigate(`/risk-assessment/${result.nextQuestion.slug}?direct=true`);
            return;
          } else {
            console.error('[Complete] No next question returned from API, falling back to base route');
            navigate('/risk-assessment');
            return;
          }
        } catch (error) {
          console.error('Failed to submit questionnaire answer:', error);
          // Fall through to step 5 navigation on error
        }
      }

      // Traditional flow or error: navigate to step 5 (success page)
      const url = source ? `${ROUTE_LABS_ORDER_STEP_5}?source=${source}` : ROUTE_LABS_ORDER_STEP_5;
      navigate(url);
    } catch (error) {
      console.error('Error confirming lab order:', error);
      console.log('[Step3] source:', source, 'questionnaireId:', questionnaireId, 'responseId:', responseId);

      // In risk assessment context, submit error answer and let user continue
      if (source === 'risk_assessment' && questionnaireId && responseId) {
        console.log('[Step3] Attempting to submit error answer and navigate away');
        try {
          const errorAnswerInput = formatAnswerForSubmission({
            value: 'error_ordering_labs',
            questionType: 'select',
            units: null,
          });

          if (errorAnswerInput) {
            const result = await submitAnswerMutation.mutateAsync({
              questionnaireId,
              responseId,
              questionSlug: 'labs_order_status',
              data: errorAnswerInput,
            });

            // Navigate to next question with direct flag
            if (result?.nextQuestion?.slug) {
              navigate(`/risk-assessment/${result.nextQuestion.slug}?direct=true`);
              return;
            } else {
              navigate('/risk-assessment');
              return;
            }
          }
        } catch (submitError) {
          console.error('Failed to submit error answer:', submitError);
          // Even if submitting the error answer failed, navigate away to prevent user from getting stuck
          navigate('/risk-assessment');
          return;
        }
      }

      // Traditional flow error handling (only reached if not risk assessment context)
      // Extract error details
      let errorMsg = 'Failed to confirm order. Please try again.';
      let status: number | undefined;

      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as { response?: { status?: number; data?: { detail?: string } } }).response;
        status = response?.status;
        if (response?.data?.detail) {
          errorMsg = response.data.detail;
        }
      }

      // Only treat as fatal error if it's a true 500+ server error
      // AND doesn't have a specific error message (which indicates it's recoverable)
      const isFatalError = status && status >= 500 && !errorMsg.includes('Failed to create order');

      if (isFatalError) {
        // For fatal errors, navigate to step 5 with error flag
        const errorUrl = source
          ? `${ROUTE_LABS_ORDER_STEP_5}?error=true&source=${source}`
          : `${ROUTE_LABS_ORDER_STEP_5}?error=true`;
        navigate(errorUrl);
        return;
      }

      // For all other errors (including lab partner failures), show inline error message
      setErrorMessage(errorMsg);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    const source = searchParams.get('source');
    const questionnaireId = searchParams.get('questionnaire_id');
    const responseId = searchParams.get('response_id');

    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (questionnaireId) params.set('questionnaire_id', questionnaireId);
    if (responseId) params.set('response_id', responseId);

    const url = params.toString() ? `${ROUTE_LABS_ORDER_STEP_2}?${params.toString()}` : ROUTE_LABS_ORDER_STEP_2;
    navigate(url);
  };

  const handleZipCodeChange = (value: string) => {
    const cleanedValue = value.replace(/\D/g, '').slice(0, 5);
    setZipCode(cleanedValue);
  };

  const openInMaps = (lab: PatientServiceCenterDTO) => {
    const address = encodeURIComponent(
      `${lab.metadata.address}, ${lab.metadata.city}, ${lab.metadata.state} ${lab.metadata.zip}`,
    );
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  // Show loading state while checking for order
  if (isLoadingOrder) {
    return (
      <LabsOrderStepLayout currentStep={3} onBack={handleBack}>
        <div className="py-8 text-center">
          <p className="text-[#9E9E9E]">Loading...</p>
        </div>
      </LabsOrderStepLayout>
    );
  }

  // Allow user to provide zip code even if none is saved
  return (
    <LabsOrderStepLayout currentStep={3} onBack={handleBack}>
      {/* Intro Text */}
      <p className="mb-6 text-[14px] leading-[170%] text-[#3B3B3B] opacity-80">
        We're showing the closest lab locations based on your zip code. Select the one that works best for you to
        continue.
      </p>

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-[14px] text-red-800">
          {errorMessage}
        </div>
      )}

      {/* Search Controls */}
      <div className="mb-6 space-y-4">
        {/* Zip Code with Location */}
        <LocationInput
          id="zipCode"
          label="Zip Code"
          value={zipCode}
          onChange={handleZipCodeChange}
          maxLength={5}
          onUseCurrentLocation={handleUseCurrentLocation}
          isGettingLocation={isGettingLocation}
        />

        {/* Search Radius */}
        <FormSelect
          id="radius"
          label="Select Search Radius"
          value={String(radius)}
          onChange={(value) => setRadius(Number(value) as typeof radius)}
          options={[
            { value: '10', label: '10 miles' },
            { value: '20', label: '20 miles' },
            { value: '25', label: '25 miles' },
            { value: '50', label: '50 miles' },
            { value: '100', label: '100 miles' },
          ]}
        />
      </div>

      {/* Lab List */}
      <div className="mb-6 space-y-3">
        {!zipCode || zipCode.length < 5 ? (
          <div className="py-8 text-center text-[#9E9E9E]">Please enter a 5-digit zip code to see available labs.</div>
        ) : isLoading ? (
          <div className="py-8 text-center text-[#9E9E9E]">Loading labs...</div>
        ) : error ? (
          <div className="py-8 text-center text-red-600">Error loading labs. Please try again.</div>
        ) : labs.length === 0 ? (
          <div className="py-8 text-center text-[#3B3B3B]">
            There were no labs found in your area. Please try expanding your search radius to see more options.
          </div>
        ) : (
          labs.map((lab) => (
            <div
              key={lab.siteCode}
              onClick={() => handleSelectLab(lab.siteCode)}
              style={{
                borderWidth: selectedLabSiteCode === lab.siteCode ? '2px' : '1px',
                borderStyle: 'solid',
                borderColor: selectedLabSiteCode === lab.siteCode ? '#0C6A58' : '#E5E5E5',
              }}
              className={`relative cursor-pointer rounded-lg p-4 transition-all ${
                selectedLabSiteCode === lab.siteCode ? 'shadow-sm' : 'hover:border-[#BDBDBD] hover:shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-4">
                  <h3 className="mb-2 text-[18px] leading-[24px] font-bold text-[#183344]">
                    {lab.metadata.labName || `Lab ${lab.siteCode}`}
                  </h3>
                  <p className="text-[14px] leading-[140%] font-normal text-[#63605D]">
                    {lab.metadata.address}
                    <br />
                    {lab.metadata.city}, {lab.metadata.state} {lab.metadata.zip}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openInMaps(lab);
                    }}
                    className="mt-2 inline-flex items-center gap-1 text-[14px] leading-[140%] font-bold text-[#183344] underline transition-colors hover:text-[#0D1F2D]"
                  >
                    See Location
                    <svg
                      width="13"
                      height="12"
                      viewBox="0 0 13 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="inline-block"
                    >
                      <path
                        d="M11.1668 1.33301L2.16683 10.333M11.1668 1.33301H3.66683M11.1668 1.33301V8.83301"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <div className="flex-shrink-0">
                  {selectedLabSiteCode === lab.siteCode ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                      <circle cx="9.5" cy="10" r="9" fill="#0C6A58" />
                      <circle cx="9.5" cy="10" r="9" stroke="#0C6A58" />
                      <circle cx="9.5" cy="10" r="9" stroke="black" strokeOpacity="0.2" />
                    </svg>
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-[#BDBDBD]"></div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Spacer to prevent content from being hidden behind sticky button */}
      <div className="h-20" />

      {/* Action Buttons - Sticky */}
      {selectedLab && (
        <div className="fixed right-0 bottom-0 left-0 z-10 border-t border-gray-200 bg-white p-4 shadow-lg">
          <div className="mx-auto flex max-w-2xl justify-center">
            <button
              type="button"
              onClick={handleContinue}
              disabled={isSubmitting}
              className="btn-primary"
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </LabsOrderStepLayout>
  );
}
